// src/pages/admin/AdminSkills.js
// Manage Skill Share listings — add, edit, approve pending, hide, delete

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllSkillListings, updateSkillListing, deleteSkillListing, createSkillListing } from '../../lib/db';
import { SKILL_TYPES, SKILL_CATEGORIES } from '../SkillsPage';
import { useAuth } from '../../lib/AuthContext';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const EMPTY = { type: 'offer_skill', category: 'other', title: '', description: '', contactEmail: '', contactPhone: '' };

const AdminSkills = () => {
  const navigate = useNavigate();
  const { user, isTownAdmin, townAdminScope } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const all = await getAllSkillListings();
    setListings(isTownAdmin ? all.filter(l => l.townId === townAdminScope) : all);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const approve = async (id) => {
    await updateSkillListing(id, { status: 'live' });
    toast.success('Approved — now live');
    load();
  };

  const hide = async (id) => {
    await updateSkillListing(id, { status: 'hidden' });
    toast.success('Hidden from public view');
    load();
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteSkillListing(id);
    toast.success('Deleted');
    load();
  };

  const startEdit = (l) => {
    setForm({
      type: l.type || 'offer_skill', category: l.category || 'other', title: l.title || '',
      description: l.description || '', contactEmail: l.contactEmail || '', contactPhone: l.contactPhone || '',
    });
    setEditingId(l.id);
    setAdding(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => { setForm(EMPTY); setAdding(false); setEditingId(null); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await updateSkillListing(editingId, form);
        toast.success('Listing updated!');
      } else {
        const townId = isTownAdmin ? townAdminScope : (localStorage.getItem('gather-town-id') || 'loughrea');
        await createSkillListing({ ...form, townId, postedBy: user.uid, postedByName: 'Gather Team' });
        toast.success('Listing added!');
      }
      cancelForm();
      load();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const filtered = listings.filter(l =>
    (!search || l.title?.toLowerCase().includes(search.toLowerCase())) &&
    (statusFilter === 'all' || l.status === statusFilter) &&
    (categoryFilter === 'all' || l.category === categoryFilter)
  );

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>← Admin</button>
      <div style={s.h1}>Skill Share</div>

      <button
        onClick={() => (adding ? cancelForm() : setAdding(true))}
        style={{ width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 14, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', marginBottom: 14 }}
      >
        {adding ? '✕ Cancel' : '+ Add listing'}
      </button>

      {adding && (
        <div style={{ background: 'var(--white)', borderRadius: 16, padding: 16, marginBottom: 16, boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink)', marginBottom: 12 }}>{editingId ? '✏️ Edit listing' : '+ New listing'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <select className="form-input" value={form.type} onChange={e => set('type', e.target.value)}>
              {Object.entries(SKILL_TYPES).map(([val, t]) => <option key={val} value={val}>{t.emoji} {t.label}</option>)}
            </select>
            <select className="form-input" value={form.category} onChange={e => set('category', e.target.value)}>
              {SKILL_CATEGORIES.filter(c => c.value !== 'all').map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
            </select>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Title" />
            <textarea className="form-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Description" rows={3} style={{ resize: 'vertical' }} />
            <input className="form-input" value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} placeholder="Contact email (optional)" />
            <input className="form-input" value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} placeholder="Contact phone (optional)" />
            <button onClick={handleSave} disabled={saving} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: 13, fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : editingId ? 'Save changes' : 'Add listing'}
            </button>
          </div>
        </div>
      )}

      <input
        style={s.search}
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by title..."
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select style={s.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="live">Live</option>
          <option value="pending_review">Pending review</option>
          <option value="hidden">Hidden</option>
        </select>
        <select style={s.filterSelect} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="all">All categories</option>
          {SKILL_CATEGORIES.filter(c => c.value !== 'all').map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No listings match.</div>
      ) : filtered.map(l => {
        const t = SKILL_TYPES[l.type] || SKILL_TYPES.offer_skill;
        return (
          <div key={l.id} style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ ...s.badge, background: t.color, color: 'white', marginBottom: 6 }}>{t.emoji} {t.label}</div>
                <div style={s.title}>{l.title}</div>
                <div style={s.meta}>
                  {l.postedByName} · {SKILL_CATEGORIES.find(c => c.value === l.category)?.label || l.category}
                </div>
              </div>
              <span style={{
                ...s.badge,
                background: l.status === 'live' ? 'var(--sage-pale)' : l.status === 'pending_review' ? '#FFF3CD' : '#F0F0EE',
                color: l.status === 'live' ? 'var(--sage-deep)' : l.status === 'pending_review' ? '#8A6D1D' : 'var(--muted)',
              }}>
                {l.status === 'pending_review' ? 'Pending review' : l.status === 'hidden' ? 'Hidden' : 'Live'}
              </span>
            </div>
            {l.description && <div style={s.desc}>{l.description}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {l.status !== 'live' && (
                <button style={s.approveBtn} onClick={() => approve(l.id)}>Approve</button>
              )}
              {l.status === 'live' && (
                <button style={s.editBtn} onClick={() => hide(l.id)}>Hide</button>
              )}
              <button style={s.editBtn} onClick={() => startEdit(l)}>Edit</button>
              <button style={s.deleteBtn} onClick={() => handleDelete(l.id, l.title)}>Delete</button>
            </div>
          </div>
        );
      })}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  page: { padding: '52px 20px 40px', background: 'var(--cream)', minHeight: '100vh' },
  back: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 12 },
  h1: { fontSize: 22, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  search: { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9', fontSize: 14, fontFamily: 'Nunito', marginBottom: 12, boxSizing: 'border-box', background: 'var(--white)' },
  filterSelect: { flex: 1, padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)' },
  card: { background: 'var(--white)', borderRadius: 14, padding: 14, marginBottom: 10, border: '1.5px solid var(--border)' },
  badge: { display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 100, padding: '4px 10px' },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--ink)' },
  meta: { fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginTop: 2 },
  desc: { fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 },
  approveBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-deep)', color: 'white', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  editBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
  deleteBtn: { flex: 1, minWidth: 80, padding: '9px 12px', borderRadius: 10, border: 'none', background: '#FBEAE7', color: '#C0392B', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'Nunito' },
};

export default AdminSkills;
